// src/components/Home.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../layout/Header';
import Navigation from '../layout/Navigation';

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800;900&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,300&display=swap');

  :root {
    --accent: #6c63ff;
    --accent-2: #8b5cf6;
    --green: #22c55e;
  }

  .home-root * { font-family: 'DM Sans', sans-serif; }

  /* ── orbs ── */
  @keyframes orb-drift {
    0%,100% { transform: translate(0,0) scale(1); }
    33%      { transform: translate(40px,-30px) scale(1.08); }
    66%      { transform: translate(-20px,20px) scale(0.94); }
  }
  .orb { position:absolute; border-radius:50%; filter:blur(80px); animation: orb-drift 14s ease-in-out infinite; pointer-events:none; }
  .orb-1 { width:500px;height:500px;background:radial-gradient(circle,#6c63ff,transparent 70%); opacity:.3; top:-160px;left:-120px; }
  .orb-2 { width:360px;height:360px;background:radial-gradient(circle,#a78bfa,transparent 70%); opacity:.25; bottom:-80px;right:-60px; animation-delay:-5s; }
  .orb-3 { width:280px;height:280px;background:radial-gradient(circle,#38bdf8,transparent 70%); opacity:.2; top:40%;left:55%; animation-delay:-9s; }
  .orb-dk-1 { opacity:.2; background:radial-gradient(circle,#4338ca,transparent 70%); }
  .orb-dk-2 { opacity:.15; background:radial-gradient(circle,#7c3aed,transparent 70%); }
  .orb-dk-3 { opacity:.12; background:radial-gradient(circle,#0ea5e9,transparent 70%); }

  /* ── hero entrance ── */
  @keyframes hero-in {
    from { opacity:0; transform: translateY(36px); }
    to   { opacity:1; transform: translateY(0); }
  }
  .hero-tag  { animation: hero-in .5s ease both; animation-delay:.1s; }
  .hero-h1   { animation: hero-in .55s ease both; animation-delay:.22s; }
  .hero-sub  { animation: hero-in .55s ease both; animation-delay:.36s; }
  .hero-btns { animation: hero-in .55s ease both; animation-delay:.48s; }
  .hero-cards{ animation: hero-in .6s ease both; animation-delay:.62s; }

  /* ── feature cards ── */
  .feat-card {
    transition: transform .25s cubic-bezier(.34,1.56,.64,1), box-shadow .25s ease;
    cursor: default;
  }
  .feat-card:hover { transform: translateY(-6px) scale(1.02); }

  /* ── button shine ── */
  @keyframes shine { from{left:-120%} to{left:120%} }
  .btn-shine { position:relative; overflow:hidden; }
  .btn-shine::after {
    content:''; position:absolute; top:0; left:-120%;
    width:60%; height:100%;
    background:linear-gradient(90deg,transparent,rgba(255,255,255,.28),transparent);
    transform:skewX(-20deg);
  }
  .btn-shine:hover::after { animation:shine .55s ease; }

  /* ── floating emoji ── */
  @keyframes float-a { 0%,100%{transform:translateY(0) rotate(-4deg)} 50%{transform:translateY(-10px) rotate(4deg)} }
  @keyframes float-b { 0%,100%{transform:translateY(0) rotate(2deg)} 50%{transform:translateY(-8px) rotate(-3deg)} }
  @keyframes float-c { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
  .float-a { animation: float-a 4s ease-in-out infinite; }
  .float-b { animation: float-b 3.5s ease-in-out infinite; animation-delay:.8s; }
  .float-c { animation: float-c 5s ease-in-out infinite; animation-delay:1.6s; }

  /* ── grid dot pattern ── */
  .dot-grid {
    background-image: radial-gradient(circle, rgba(108,99,255,.18) 1px, transparent 1px);
    background-size: 28px 28px;
  }

  /* ── gradient text ── */
  .grad-text {
    background: linear-gradient(135deg, #6c63ff 0%, #a78bfa 50%, #38bdf8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .grad-text-dk {
    background: linear-gradient(135deg, #a78bfa 0%, #c4b5fd 50%, #7dd3fc 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  /* ── stat counter ── */
  @keyframes count-in { from{opacity:0;transform:translateY(12px)} to{opacity:1;transform:translateY(0)} }
  .stat-item { animation: count-in .5s ease both; }
  .stat-item:nth-child(1){animation-delay:.7s}
  .stat-item:nth-child(2){animation-delay:.85s}
  .stat-item:nth-child(3){animation-delay:1s}
`;

const FEATURES = [
  {
    emoji: '🤖',
    floatClass: 'float-a',
    title: 'Intelligent Bots',
    desc: 'Deploy smart automation bots that adapt to your agency\'s workflows out of the box.',
    accent: 'from-violet-500/10 to-indigo-500/10',
    border: 'border-violet-500/20',
  },
  {
    emoji: '⚡',
    floatClass: 'float-b',
    title: 'Lightning Fast',
    desc: 'Process thousands of permit applications, inspections, and requests in minutes.',
    accent: 'from-blue-500/10 to-cyan-500/10',
    border: 'border-blue-500/20',
  },
  {
    emoji: '🛡️',
    floatClass: 'float-c',
    title: 'Gov-Grade Security',
    desc: 'Enterprise security built for government agencies — encrypted, audited, compliant.',
    accent: 'from-emerald-500/10 to-green-500/10',
    border: 'border-emerald-500/20',
  },
];

const STATS = [
  { value: '50+', label: 'Agencies' },
  { value: '2M+', label: 'Processed' },
  { value: '99.9%', label: 'Uptime' },
];

const Home = () => {
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    setIsDarkMode(savedTheme === 'dark' || (!savedTheme && prefersDark));
  }, []);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
  };

  return (
    <>
      <style>{STYLES}</style>

      <div className={`home-root flex flex-col min-h-screen transition-colors duration-500 overflow-x-hidden ${
        isDarkMode
          ? 'bg-gradient-to-br from-[#0a0a1a] via-[#0f0f24] to-[#14102a]'
          : 'bg-gradient-to-br from-[#f0f0fb] via-[#f8f8ff] to-[#eeeef8]'
      }`}>

        {/* Background orbs */}
        <div className="fixed inset-0 pointer-events-none overflow-hidden" style={{ zIndex: 0 }}>
          <div className={`orb orb-1 ${isDarkMode ? 'orb-dk-1' : ''}`} />
          <div className={`orb orb-2 ${isDarkMode ? 'orb-dk-2' : ''}`} />
          <div className={`orb orb-3 ${isDarkMode ? 'orb-dk-3' : ''}`} />
        </div>

        {/* Dot grid overlay */}
        <div className={`fixed inset-0 pointer-events-none dot-grid transition-opacity duration-500 ${isDarkMode ? 'opacity-30' : 'opacity-60'}`} style={{ zIndex: 1 }} />

        <div className="relative" style={{ zIndex: 10 }}>
          <Header isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
          <Navigation isDarkMode={isDarkMode} toggleDarkMode={toggleDarkMode} />
        </div>

        {/* ── MAIN ── */}
        <main className="flex-grow flex flex-col items-center justify-start pt-[150px] pb-20 px-6 relative" style={{ zIndex: 5 }}>

          {/* ── HERO ── */}
          <section className="w-full max-w-4xl text-center">

            {/* badge */}
            <div className="hero-tag inline-flex items-center gap-2 mb-6 px-4 py-1.5 rounded-full text-xs font-semibold tracking-widest uppercase border backdrop-blur-sm"
              style={{
                background: isDarkMode ? 'rgba(108,99,255,.12)' : 'rgba(108,99,255,.08)',
                borderColor: isDarkMode ? 'rgba(108,99,255,.3)' : 'rgba(108,99,255,.2)',
                color: isDarkMode ? '#a78bfa' : '#6c63ff',
                fontFamily: 'Sora, sans-serif',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
              Government Automation Platform
            </div>

            {/* headline */}
            <h1
              className="hero-h1 text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-[1.05] tracking-tight"
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              <span className={isDarkMode ? 'text-white' : 'text-gray-900'}>Welcome to </span>
              <span className={isDarkMode ? 'grad-text-dk' : 'grad-text'}>BOTABLE</span>
            </h1>

            {/* subheadline */}
            <p
              className={`hero-sub text-lg md:text-xl max-w-2xl mx-auto leading-relaxed mb-10 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}
            >
              Create, manage, and deploy intelligent bots for your agency.
              Streamline permit workflows, inspections, and compliance — all in one place.
            </p>

            {/* CTA buttons */}
            <div className="hero-btns flex flex-col sm:flex-row gap-4 justify-center items-center mb-16">
              <Link
                to="/signup"
                className="btn-shine inline-flex items-center gap-2 py-4 px-9 bg-gradient-to-r from-[#6c63ff] to-[#8b5cf6] hover:from-[#5b52ee] hover:to-[#7c3aed] text-white font-bold text-base rounded-2xl transition-all duration-200 hover:scale-[1.04] active:scale-95 shadow-xl hover:shadow-violet-500/40"
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                🚀 Get Started — It's Free
              </Link>
              <Link
                to="/signin"
                className={`inline-flex items-center gap-2 py-4 px-9 font-semibold text-base rounded-2xl border-2 transition-all duration-200 hover:scale-[1.03] active:scale-95 ${
                  isDarkMode
                    ? 'border-gray-700 text-gray-300 hover:bg-white/5 hover:border-gray-500'
                    : 'border-gray-300 text-gray-700 hover:bg-white hover:border-violet-300 hover:text-violet-700'
                }`}
                style={{ fontFamily: 'Sora, sans-serif' }}
              >
                Sign In →
              </Link>
            </div>

            {/* ── STATS ── */}
            <div className="hero-cards flex justify-center gap-10 mb-16">
              {STATS.map((s, i) => (
                <div key={i} className={`stat-item text-center`}>
                  <p
                    className={`text-3xl font-black ${isDarkMode ? 'grad-text-dk' : 'grad-text'}`}
                    style={{ fontFamily: 'Sora, sans-serif' }}
                  >
                    {s.value}
                  </p>
                  <p className={`text-xs font-medium mt-0.5 uppercase tracking-widest ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                    {s.label}
                  </p>
                </div>
              ))}
            </div>

            {/* ── FEATURE CARDS ── */}
            <div className="hero-cards grid grid-cols-1 sm:grid-cols-3 gap-5">
              {FEATURES.map((f, i) => (
                <div
                  key={i}
                  className={`feat-card relative rounded-3xl border p-6 text-left bg-gradient-to-br ${f.accent} ${f.border} backdrop-blur-sm ${
                    isDarkMode ? 'bg-white/4' : 'bg-white/60'
                  }`}
                  style={{
                    boxShadow: isDarkMode
                      ? '0 8px 32px rgba(0,0,0,.3), inset 0 1px 0 rgba(255,255,255,.04)'
                      : '0 8px 32px rgba(108,99,255,.08), inset 0 1px 0 rgba(255,255,255,.8)',
                  }}
                >
                  <span className={`text-4xl mb-4 block ${f.floatClass}`}>{f.emoji}</span>
                  <h3
                    className={`text-base font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}
                    style={{ fontFamily: 'Sora, sans-serif' }}
                  >
                    {f.title}
                  </h3>
                  <p className={`text-sm leading-relaxed ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {f.desc}
                  </p>
                </div>
              ))}
            </div>
          </section>
        </main>

        {/* ── FOOTER ── */}
        <footer
          className={`relative z-10 border-t py-6 px-6 transition-colors duration-500 ${
            isDarkMode ? 'border-white/5 bg-[#0a0a1a]/80' : 'border-gray-200/60 bg-white/50'
          } backdrop-blur-md`}
        >
          <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
            <p
              className={`text-sm font-bold ${isDarkMode ? 'grad-text-dk' : 'grad-text'}`}
              style={{ fontFamily: 'Sora, sans-serif' }}
            >
              BOTABLE
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              1234 Tech Lane, Innovation City, IC 56789
            </p>
            <p className={`text-xs ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
              contact@botable.com · (123) 456-7890
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Home;